import { Link } from 'react-router';
import { useLocale, useT } from '../i18n/locale-context';
import { localePath } from '../i18n/locale-path';

interface OrgHintProps {
  /** What the user typed — the bare ИНН/СТИР, always quoted back. */
  q: string;
  hints: Array<{ tin: string; kind: 'buyer' | 'supplier'; name: string | null }>;
}

/**
 * "That number is an organisation — here is its profile."
 *
 * Replaying real searches (`events.search_performed`) showed users PASTE bare
 * 9-digit ИНН/СТИР into the search box — `305502107` (11 searches), `301035105`
 * (7) — expecting the company, while full-text match returns nothing. When the
 * number resolves in the award graph the API sends `orgHints`; this offers the
 * buyer/supplier profile rather than the empty-results card.
 */
export function OrgHint({ q, hints }: OrgHintProps) {
  const t = useT();
  const { locale } = useLocale();
  if (hints.length === 0) return null;
  const href = (h: OrgHintProps['hints'][number]) =>
    localePath(
      `/${h.kind === 'buyer' ? 'buyer' : 'supplier'}/${encodeURIComponent(h.tin)}`,
      locale,
    );

  return (
    <div className="mb-4 rounded-lg border border-edge bg-bone/60 px-4 py-3 text-sm text-ink">
      <p className="mb-2">{t('org.hint.intro', { q })}</p>
      <ul className="flex flex-wrap gap-2">
        {hints.map((h) => (
          <li key={h.kind}>
            <Link
              to={href(h)}
              className="inline-block rounded-full border border-edge px-3 py-1 text-xs font-medium text-brand hover:bg-bone"
            >
              {t(h.kind === 'buyer' ? 'org.hint.buyer' : 'org.hint.supplier', {
                name: h.name ?? h.tin,
              })}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
